import React, { useEffect, useMemo, useState } from "react"
import {
    Alert,
    Box,
    Button,
    Chip,
    CircularProgress,
    Grid,
    MenuItem,
    Paper,
    Snackbar,
    Stack,
    TextField,
    Typography,
} from "@mui/material"
import ArrowBackIcon from "@mui/icons-material/ArrowBack"
import EditIcon from "@mui/icons-material/Edit"
import SaveIcon from "@mui/icons-material/Save"
import CloseIcon from "@mui/icons-material/Close"
import { useTranslation } from "react-i18next"
import { useNavigate, useParams } from "react-router-dom"
import apiClient from "../../services/apiClient"
import * as adminService from "../../services/adminService"
import PricingGridTable, {
    PricingGridDraft,
    PricingGridRow,
} from "../../components/pricing/PricingGridTable"
import { listCategories } from "../../services/categoriesService"
import { listCustomerTypes } from "../../services/customerTypesService"
import SimpleSalesStats from "../../components/stats/SimpleSalesStats"
import {
    getItem,
    getItemStats,
    updateItem,
    ItemDetail,
    ItemStatsPayload,
} from "../../services/itemsService"
import {
    createItemPricingRule,
    deleteItemPricingRule,
    ItemPricingRule,
    listItemPricingRules,
    resolveItemPrice,
    ResolvedItemPrice,
    updateItemPricingRule,
} from "../../services/pricingService"
import { getOutletVatRates } from "../../services/outletSettingsService";
type ItemFormState = {
    name: string
    description: string
    vatRateId: string
    barcode: string
    categoryId: string
    outletId: string
    qty: string
    stockAlertThreshold: string
    unitPrice: string
    purchasePrice: string
}
type VatRateOption = {
    id: string;
    label: string;
    percentage: number;
    is_enabled: boolean;
    is_active: boolean;
};
const UUID_REGEX =
    /^[0-9a-f]{8}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{12}$/i

function toFormState(item: ItemDetail): ItemFormState {
    return {
        name: item.name || "",
        description: item.description || "",
        vatRateId: item.vat_rate_id || "",
        barcode: item.barcode || "",
        categoryId: item.category_id || "",
        outletId: item.outlet_id || "",
        qty: item.qty != null ? String(item.qty) : "0",
        stockAlertThreshold:
            item.stock_alert_threshold != null
                ? String(item.stock_alert_threshold)
                : "",
        unitPrice:
            item.unit_price_minor != null
                ? (Number(item.unit_price_minor) / 100).toFixed(2)
                : "0.00",
        purchasePrice:
            item.purchase_price_minor != null
                ? (Number(item.purchase_price_minor) / 100).toFixed(2)
                : "0.00",
    }
}

const ItemDetailPage: React.FC = () => {
    const { t, i18n } = useTranslation()
    const { itemId } = useParams<{ itemId: string }>()
    const navigate = useNavigate()

    const formatMinor = (value?: number | null) =>
        new Intl.NumberFormat(i18n.language || "fr", {
            style: "currency",
            currency: "EUR",
        }).format(Number(value || 0) / 100)
    function parseVatRatePercent(vatRateId: string | undefined | null): number {
        const raw = String(vatRateId || "").trim();
        const match = raw.match(/(\d+(?:[\.,]\d+)?)/);
        if (!match) return 0;

        const parsed = Number.parseFloat(match[1].replace(",", "."));
        return Number.isFinite(parsed) && parsed >= 0 ? parsed : 0;
    }
    const [companyId, setCompanyId] = useState("")
    const [item, setItem] = useState<ItemDetail | null>(null)
    const [form, setForm] = useState<ItemFormState>({
        name: "",
        description: "",
        vatRateId: "",
        barcode: "",
        categoryId: "",
        outletId: "",
        qty: "0",
        stockAlertThreshold: "",
        unitPrice: "0.00",
        purchasePrice: "0.00",
    })
    const [categories, setCategories] = useState<any[]>([])
    const [outlets, setOutlets] = useState<any[]>([])
    const [vatRateOptions, setVatRateOptions] = useState<VatRateOption[]>([]);
    const [loading, setLoading] = useState(true)
    const [notFound, setNotFound] = useState(false)
    const [loadError, setLoadError] = useState<string | null>(null)
    const [saving, setSaving] = useState(false)
    const [statsLoading, setStatsLoading] = useState(false)
    const [stats, setStats] = useState<ItemStatsPayload | null>(null)
    const [pricingRules, setPricingRules] = useState<ItemPricingRule[]>([])
    const [pricingLoading, setPricingLoading] = useState(false)
    const [customerTypeOptions, setCustomerTypeOptions] = useState<
        { id: string; label: string }[]
    >([])
    const [defaultPriceGridId, setDefaultPriceGridId] = useState("")
    const [resolvedPrice, setResolvedPrice] =
        useState<ResolvedItemPrice | null>(null)
    const [isEditing, setIsEditing] = useState(false)
    const [snackbar, setSnackbar] = useState<{
        open: boolean
        message: string
        severity: "success" | "error"
    }>({
        open: false,
        message: "",
        severity: "success",
    })

    useEffect(() => {
        let cancelled = false
            ; (async () => {
                try {
                    const res = await apiClient.get("/api/companies")
                    if (cancelled) return
                    const companies = res.companies || []
                    if (companies.length > 0) {
                        setCompanyId(companies[0].id)
                    }
                } catch (error) {
                    console.error("Failed to load companies:", error)
                }
            })()

        return () => {
            cancelled = true
        }
    }, [])

    useEffect(() => {
        if (!companyId) return

        if (!itemId || !UUID_REGEX.test(itemId)) {
            setItem(null)
            setNotFound(true)
            setLoadError(null)
            setLoading(false)
            return
        }

        let cancelled = false
            ; (async () => {
                setLoading(true)
                setNotFound(false)
                setLoadError(null)
                try {
                    const loadedItem = await getItem(companyId, itemId)
                    if (cancelled) return

                    setItem(loadedItem)
                    setForm(toFormState(loadedItem))

                    const [outletsResult, categoriesResult] =
                        await Promise.allSettled([
                            apiClient.get(`/api/outlets?company_id=${companyId}`),
                            listCategories(companyId, {
                                includeArchived: true,
                                page: 1,
                                pageSize: 200,
                            }),
                        ])

                    if (cancelled) return

                    if (outletsResult.status === "fulfilled") {
                        setOutlets(outletsResult.value.outlets || [])
                    }

                    if (categoriesResult.status === "fulfilled") {
                        setCategories(categoriesResult.value.categories || [])
                    }
                } catch (error) {
                    console.error("Failed to load item detail:", error)
                    if (!cancelled) {
                        const err: any = error
                        setItem(null)
                        setNotFound(err?.statusCode === 404)
                        if (err?.statusCode !== 404) {
                            setLoadError(
                                err?.message ||
                                String(t("itemDetailPage.errors.loadFailed"))
                            )
                        }
                    }
                } finally {
                    if (!cancelled) {
                        setLoading(false)
                        setIsEditing(false)
                    }
                }
            })()

        return () => {
            cancelled = true
        }
    }, [companyId, itemId, t])

    useEffect(() => {
        if (!companyId) return

        let cancelled = false
            ; (async () => {
                try {
                    const [customerTypesResult, gridsResult] = await Promise.allSettled(
                        [
                            listCustomerTypes(companyId),
                            adminService.getPriceGrids(companyId, {
                                activeOnly: true,
                            }),
                        ]
                    )

                    if (cancelled) return

                    if (customerTypesResult.status === "fulfilled") {
                        const mapped = (customerTypesResult.value || []).map(
                            (ct: any) => ({
                                id: ct.id,
                                label: ct.name || ct.id,
                            })
                        )
                        setCustomerTypeOptions(mapped)
                    }

                    if (gridsResult.status === "fulfilled") {
                        const grids = gridsResult.value || []
                        const preferredGrid =
                            grids.find(
                                (grid: any) =>
                                    grid.scope === "company" && grid.is_active
                            ) ||
                            grids.find((grid: any) => grid.is_active) ||
                            grids[0] ||
                            null
                        setDefaultPriceGridId(preferredGrid?.id || "")
                    }
                } catch (error) {
                    console.error(
                        "Failed to load item pricing support data:",
                        error
                    )
                }
            })()

        return () => {
            cancelled = true
        }
    }, [companyId])
    useEffect(() => {
        if (!companyId) return;

        const effectiveOutletId = form.outletId || item?.outlet_id || outlets[0]?.id;
        if (!effectiveOutletId) {
            setVatRateOptions([]);
            return;
        }

        let cancelled = false;
        (async () => {
            try {
                const rates = await getOutletVatRates(companyId, effectiveOutletId, {
                    enabledOnly: true,
                });
                if (!cancelled) {
                    setVatRateOptions(rates || []);
                }
            } catch {
                if (!cancelled) {
                    setVatRateOptions([]);
                }
            }
        })();

        return () => {
            cancelled = true;
        };
    }, [companyId, form.outletId, item?.outlet_id, outlets]);

    useEffect(() => {
        if (vatRateOptions.length === 0) return;

        const isCurrentValueAllowed = vatRateOptions.some(
            (rate) => rate.id === form.vatRateId,
        );

        if (isCurrentValueAllowed) return;

        setForm((prev) => ({ ...prev, vatRateId: vatRateOptions[0].id }));
    }, [form.vatRateId, vatRateOptions]);
    useEffect(() => {
        if (!companyId || !itemId) return

        let cancelled = false
            ; (async () => {
                setPricingLoading(true)
                try {
                    const [rules, resolved] = await Promise.all([
                        listItemPricingRules(companyId, itemId),
                        resolveItemPrice(companyId, itemId, {
                            ...(defaultPriceGridId
                                ? { priceGridId: defaultPriceGridId }
                                : {}),
                        }),
                    ])

                    if (cancelled) return
                    setPricingRules(rules || [])
                    setResolvedPrice(resolved)
                } catch (error) {
                    console.error("Failed to load item pricing data:", error)
                    if (!cancelled) {
                        setPricingRules([])
                        setResolvedPrice(null)
                    }
                } finally {
                    if (!cancelled) {
                        setPricingLoading(false)
                    }
                }
            })()

        return () => {
            cancelled = true
        }
    }, [companyId, itemId, defaultPriceGridId])

    useEffect(() => {
        if (!companyId || !itemId) return

        let cancelled = false
            ; (async () => {
                setStatsLoading(true)
                try {
                    const statsPayload = await getItemStats(companyId, itemId)
                    if (cancelled) return
                    setStats(statsPayload)
                } catch (error) {
                    console.error("Failed to load item stats:", error)
                    if (!cancelled) {
                        setStats({
                            revenue_total_minor: 0,
                            margin_unit_minor: null,
                            margin_total_minor: 0,
                            monthly_sales: [],
                        })
                    }
                } finally {
                    if (!cancelled) {
                        setStatsLoading(false)
                    }
                }
            })()

        return () => {
            cancelled = true
        }
    }, [companyId, itemId])

    const categoryName = useMemo(() => {
        return (
            categories.find((c) => c.id === item?.category_id)?.name ||
            t("common.dash")
        )
    }, [categories, item?.category_id, t])

    const outletName = useMemo(() => {
        return (
            outlets.find((o) => o.id === item?.outlet_id)?.name ||
            t("common.dash")
        )
    }, [outlets, item?.outlet_id, t])

    const pricingRows = useMemo<PricingGridRow[]>(() => {
        return pricingRules.map((rule) => {
            const customerTypeLabel =
                customerTypeOptions.find(
                    (ct) => ct.id === rule.customer_type_id
                )?.label ||
                (rule.customer_type_id
                    ? rule.customer_type_id
                    : String(t("itemDetailPage.pricing.allCustomerTypes")))

            return {
                id: rule.id,
                customerTypeId: rule.customer_type_id || null,
                customerTypeLabel,
                unitPriceMinor: Number(rule.unit_price_minor || 0),
                effectiveFrom: rule.effective_from || null,
                effectiveTo: rule.effective_to || null,
            }
        })
    }, [pricingRules, customerTypeOptions, t])

    const resolvedSourceLabel = useMemo(() => {
        const source = resolvedPrice?.resolved_from
        if (!source) return String(t("itemDetailPage.pricing.resolution.none"))

        const labels: Record<string, string> = {
            article_grid: String(
                t("itemDetailPage.pricing.resolution.articleGrid")
            ),
            category_grid: String(
                t("itemDetailPage.pricing.resolution.categoryGrid")
            ),
            article_base: String(
                t("itemDetailPage.pricing.resolution.articleBase")
            ),
            category_base: String(
                t("itemDetailPage.pricing.resolution.categoryBase")
            ),
        }

        return labels[source] || source
    }, [resolvedPrice, t])
    const selectedVatRatePercent = useMemo(() => {
        const selected = vatRateOptions.find((rate) => rate.id === form.vatRateId);
        if (selected) {
            return Number(selected.percentage || 0);
        }
        return parseVatRatePercent(form.vatRateId);
    }, [vatRateOptions, form.vatRateId]);

    const salePriceTtcDisplay = useMemo(() => {
        const unitPrice = Number.parseFloat(String(form.unitPrice || "0").replace(",", "."));
        if (!Number.isFinite(unitPrice) || unitPrice <= 0) {
            return "0.00 €";
        }

        const ttc = unitPrice * (1 + selectedVatRatePercent / 100);
        return `${ttc.toFixed(2)} €`;
    }, [form.unitPrice, selectedVatRatePercent]);
    const handleStartEdit = () => {
        if (!item) return
        setForm(toFormState(item))
        setIsEditing(true)
    }

    const handleCancel = () => {
        if (!item) return
        setForm(toFormState(item))
        setIsEditing(false)
    }

    const handleSave = async () => {
        if (!companyId || !itemId) return

        const trimmedName = form.name.trim()
        if (!trimmedName) {
            setSnackbar({
                open: true,
                message: String(t("itemDetailPage.validation.nameRequired")),
                severity: "error",
            })
            return
        }

        const qty = Number(form.qty)
        const stockAlertThresholdInput = form.stockAlertThreshold.trim()
        const unitPrice = Number(form.unitPrice.replace(",", "."))
        const purchasePrice = Number(form.purchasePrice.replace(",", "."))

        let normalizedStockAlertThreshold: number | null = null
        if (stockAlertThresholdInput !== "") {
            const threshold = Number(stockAlertThresholdInput)
            if (!Number.isInteger(threshold) || threshold < 0) {
                setSnackbar({
                    open: true,
                    message: String(
                        t(
                            "itemDetailPage.validation.stockAlertThresholdInvalid"
                        )
                    ),
                    severity: "error",
                })
                return
            }
            normalizedStockAlertThreshold = threshold
        }

        if (!Number.isFinite(qty)) {
            setSnackbar({
                open: true,
                message: String(t("itemDetailPage.validation.quantityInvalid")),
                severity: "error",
            })
            return
        }

        if (!Number.isFinite(unitPrice) || unitPrice < 0) {
            setSnackbar({
                open: true,
                message: String(
                    t("itemDetailPage.validation.salePriceInvalid")
                ),
                severity: "error",
            })
            return
        }

        if (!Number.isFinite(purchasePrice) || purchasePrice < 0) {
            setSnackbar({
                open: true,
                message: String(
                    t("itemDetailPage.validation.purchasePriceInvalid")
                ),
                severity: "error",
            })
            return
        }

        setSaving(true)
        try {
            const updated = await updateItem(companyId, itemId, {
                name: trimmedName,
                description: form.description.trim() || null,
                vat_rate_id: form.vatRateId.trim() || null,
                barcode: form.barcode.trim() || null,
                category_id: form.categoryId || null,
                outlet_id: form.outletId || null,
                qty,
                stock_alert_threshold: normalizedStockAlertThreshold,
                unit_price_minor: Math.round(unitPrice * 100),
                purchase_price_minor: Math.round(purchasePrice * 100),
            })

            setItem(updated)
            setForm(toFormState(updated))
            setIsEditing(false)
            setSnackbar({
                open: true,
                message: String(t("itemDetailPage.messages.updated")),
                severity: "success",
            })
        } catch (error: any) {
            setSnackbar({
                open: true,
                message:
                    error?.message ||
                    String(t("itemDetailPage.errors.saveFailed")),
                severity: "error",
            })
        } finally {
            setSaving(false)
        }
    }

    const handleCreatePricingRule = async (draft: PricingGridDraft) => {
        if (!companyId || !itemId || !item) return

        const priceGridId =
            defaultPriceGridId || pricingRules[0]?.price_grid_id || ""
        if (!priceGridId) {
            throw new Error(
                String(t("itemDetailPage.pricing.errors.noActiveGrid"))
            )
        }

        try {
            await createItemPricingRule(companyId, itemId, {
                ...draft,
                price_grid_id: priceGridId,
                price_type: "base",
                currency: item.currency || "EUR",
            })

            const [rules, resolved] = await Promise.all([
                listItemPricingRules(companyId, itemId),
                resolveItemPrice(companyId, itemId, {
                    ...(defaultPriceGridId
                        ? { priceGridId: defaultPriceGridId }
                        : {}),
                }),
            ])
            setPricingRules(rules || [])
            setResolvedPrice(resolved)
            setSnackbar({
                open: true,
                message: String(t("itemDetailPage.pricing.messages.ruleAdded")),
                severity: "success",
            })
        } catch (error: any) {
            const message =
                error?.message ||
                String(t("itemDetailPage.pricing.errors.createRuleFailed"))
            setSnackbar({ open: true, message, severity: "error" })
            throw error
        }
    }

    const handleUpdatePricingRule = async (
        ruleId: string,
        draft: PricingGridDraft
    ) => {
        if (!companyId || !itemId) return

        try {
            const existing = pricingRules.find((rule) => rule.id === ruleId)
            const priceGridId = existing?.price_grid_id || defaultPriceGridId

            await updateItemPricingRule(companyId, itemId, ruleId, {
                ...draft,
                ...(priceGridId ? { price_grid_id: priceGridId } : {}),
            })

            const [rules, resolved] = await Promise.all([
                listItemPricingRules(companyId, itemId),
                resolveItemPrice(companyId, itemId, {
                    ...(defaultPriceGridId
                        ? { priceGridId: defaultPriceGridId }
                        : {}),
                }),
            ])
            setPricingRules(rules || [])
            setResolvedPrice(resolved)
            setSnackbar({
                open: true,
                message: String(
                    t("itemDetailPage.pricing.messages.ruleUpdated")
                ),
                severity: "success",
            })
        } catch (error: any) {
            const message =
                error?.message ||
                String(t("itemDetailPage.pricing.errors.updateRuleFailed"))
            setSnackbar({ open: true, message, severity: "error" })
            throw error
        }
    }

    const handleDeletePricingRule = async (ruleId: string) => {
        if (!companyId || !itemId) return

        try {
            await deleteItemPricingRule(companyId, itemId, ruleId)

            const [rules, resolved] = await Promise.all([
                listItemPricingRules(companyId, itemId),
                resolveItemPrice(companyId, itemId, {
                    ...(defaultPriceGridId
                        ? { priceGridId: defaultPriceGridId }
                        : {}),
                }),
            ])
            setPricingRules(rules || [])
            setResolvedPrice(resolved)
            setSnackbar({
                open: true,
                message: String(
                    t("itemDetailPage.pricing.messages.ruleDeleted")
                ),
                severity: "success",
            })
        } catch (error: any) {
            const message =
                error?.message ||
                String(t("itemDetailPage.pricing.errors.deleteRuleFailed"))
            setSnackbar({ open: true, message, severity: "error" })
            throw error
        }
    }

    if (loading) {
        return (
            <Box sx={{ p: 4, display: "flex", justifyContent: "center" }}>
                <CircularProgress />
            </Box>
        )
    }

    if (!item) {
        return (
            <Box sx={{ p: 3, maxWidth: 960, mx: "auto" }}>
                <Paper sx={{ p: 3 }}>
                    <Typography variant="h6" sx={{ fontWeight: 700, mb: 1 }}>
                        {notFound
                            ? t("itemDetailPage.notFound.title")
                            : t("itemDetailPage.errors.loadTitle")}
                    </Typography>
                    <Typography color="text.secondary" sx={{ mb: 2 }}>
                        {notFound
                            ? t("itemDetailPage.notFound.message")
                            : loadError ||
                            t("itemDetailPage.errors.unexpected")}
                    </Typography>
                    <Button
                        startIcon={<ArrowBackIcon />}
                        variant="outlined"
                        onClick={() => navigate("/admin/items")}
                    >
                        {t("itemDetailPage.actions.backToItems")}
                    </Button>
                </Paper>
            </Box>
        )
    }

    return (
        <Box sx={{ p: 3, maxWidth: 1100, mx: "auto" }}>
            <Stack
                direction={{ xs: "column", sm: "row" }}
                alignItems={{ xs: "stretch", sm: "center" }}
                justifyContent="space-between"
                spacing={2}
                sx={{ mb: 3 }}
            >
                <Stack direction="row" spacing={1.5} alignItems="center">
                    <Button
                        variant="text"
                        startIcon={<ArrowBackIcon />}
                        onClick={() => navigate("/admin/items")}
                    >
                        {t("itemDetailPage.actions.back")}
                    </Button>
                    <Typography variant="h5" sx={{ fontWeight: 700 }}>
                        {item.name}
                    </Typography>
                    <Chip
                        label={
                            item.is_archived
                                ? t("itemDetailPage.status.archived")
                                : t("itemDetailPage.status.active")
                        }
                        size="small"
                        color={item.is_archived ? "default" : "success"}
                        variant={item.is_archived ? "outlined" : "filled"}
                    />
                    {item.is_stock_alert ? (
                        <Chip
                            label={t("itemDetailPage.status.lowStock")}
                            size="small"
                            color="error"
                            variant="filled"
                        />
                    ) : null}
                </Stack>

                {!isEditing ? (
                    <Button
                        variant="contained"
                        startIcon={<EditIcon />}
                        onClick={handleStartEdit}
                    >
                        {t("itemDetailPage.actions.edit")}
                    </Button>
                ) : (
                    <Stack direction="row" spacing={1}>
                        <Button
                            variant="outlined"
                            color="inherit"
                            startIcon={<CloseIcon />}
                            onClick={handleCancel}
                            disabled={saving}
                        >
                            {t("common.cancel")}
                        </Button>
                        <Button
                            variant="contained"
                            startIcon={<SaveIcon />}
                            onClick={handleSave}
                            disabled={saving}
                        >
                            {saving ? t("common.saving") : t("common.save")}
                        </Button>
                    </Stack>
                )}
            </Stack>

            <Paper sx={{ p: 3, border: "1px solid #e5e7eb", mb: 2 }}>
                <Grid container spacing={2.5}>
                    <Grid item xs={12} md={6}>
                        <TextField
                            label={String(t("itemDetailPage.fields.sku"))}
                            value={item.sku || ""}
                            fullWidth
                            disabled
                        />
                    </Grid>
                    <Grid item xs={12} md={6}>
                        <TextField
                            label={String(t("itemDetailPage.fields.name"))}
                            value={form.name}
                            fullWidth
                            disabled={!isEditing || saving}
                            onChange={(e) =>
                                setForm((prev) => ({
                                    ...prev,
                                    name: e.target.value,
                                }))
                            }
                        />
                    </Grid>
                    <Grid item xs={12}>
                        <TextField
                            label={String(
                                t("itemDetailPage.fields.description")
                            )}
                            value={form.description}
                            fullWidth
                            multiline
                            minRows={3}
                            disabled={!isEditing || saving}
                            onChange={(e) =>
                                setForm((prev) => ({
                                    ...prev,
                                    description: e.target.value,
                                }))
                            }
                        />
                    </Grid>

                    <Grid item xs={12} md={4}>
                        <TextField
                            label={String(t("itemDetailPage.fields.vatCode"))}
                            value={form.vatRateId}
                            select
                            fullWidth
                            disabled={!isEditing || saving}
                            onChange={(e) =>
                                setForm((prev) => ({
                                    ...prev,
                                    vatRateId: e.target.value,
                                }))
                            }
                        >
                            {vatRateOptions.map((rate) => (
                                <MenuItem key={rate.id} value={rate.id}>
                                    {rate.label} ({Number(rate.percentage || 0).toFixed(2)}%)
                                </MenuItem>
                            ))}
                        </TextField>
                    </Grid>

                    <Grid item xs={12} md={4}>
                        <TextField
                            label={String(t("itemDetailPage.fields.barcode"))}
                            value={form.barcode}
                            fullWidth
                            disabled={!isEditing || saving}
                            onChange={(e) =>
                                setForm((prev) => ({
                                    ...prev,
                                    barcode: e.target.value,
                                }))
                            }
                        />
                    </Grid>
                    <Grid item xs={12} md={4}>
                        <TextField
                            label={String(t("itemDetailPage.fields.quantity"))}
                            value={form.qty}
                            fullWidth
                            type="number"
                            disabled={!isEditing || saving}
                            onChange={(e) =>
                                setForm((prev) => ({
                                    ...prev,
                                    qty: e.target.value,
                                }))
                            }
                        />
                    </Grid>
                    <Grid item xs={12} md={4}>
                        <TextField
                            label={String(
                                t("itemDetailPage.fields.stockAlertThreshold")
                            )}
                            value={form.stockAlertThreshold}
                            fullWidth
                            type="number"
                            inputProps={{ step: "1", min: "0" }}
                            disabled={!isEditing || saving}
                            placeholder={String(
                                t("itemDetailPage.fields.notSet")
                            )}
                            onChange={(e) =>
                                setForm((prev) => ({
                                    ...prev,
                                    stockAlertThreshold: e.target.value,
                                }))
                            }
                        />
                    </Grid>

                    <Grid item xs={12} md={6}>
                        <TextField
                            label={String(t("itemDetailPage.fields.category"))}
                            value={form.categoryId}
                            fullWidth
                            select
                            disabled={!isEditing || saving}
                            onChange={(e) =>
                                setForm((prev) => ({
                                    ...prev,
                                    categoryId: e.target.value,
                                }))
                            }
                        >
                            <MenuItem value="">
                                {t("itemDetailPage.fields.none")}
                            </MenuItem>
                            {categories.map((category) => (
                                <MenuItem key={category.id} value={category.id}>
                                    {category.name}
                                </MenuItem>
                            ))}
                        </TextField>
                    </Grid>
                    <Grid item xs={12} md={6}>
                        <TextField
                            label={String(t("itemDetailPage.fields.outlet"))}
                            value={form.outletId}
                            fullWidth
                            select
                            disabled={!isEditing || saving}
                            onChange={(e) =>
                                setForm((prev) => ({
                                    ...prev,
                                    outletId: e.target.value,
                                }))
                            }
                        >
                            <MenuItem value="">
                                {t("itemDetailPage.fields.none")}
                            </MenuItem>
                            {outlets.map((outlet) => (
                                <MenuItem key={outlet.id} value={outlet.id}>
                                    {outlet.name}
                                </MenuItem>
                            ))}
                        </TextField>
                    </Grid>

                    <Grid item xs={12} md={4}>
                        <TextField
                            label={String(t("itemDetailPage.fields.salePrice"))}
                            value={form.unitPrice}
                            fullWidth
                            type="number"
                            inputProps={{ step: "0.01", min: "0" }}
                            disabled={!isEditing || saving}
                            onChange={(e) =>
                                setForm((prev) => ({
                                    ...prev,
                                    unitPrice: e.target.value,
                                }))
                            }
                        />
                    </Grid>
                    <Grid item xs={12} md={4}>
                        <TextField
                            label={String(t("itemDetailPage.fields.salePriceTtc"))}
                            value={salePriceTtcDisplay}
                            fullWidth
                            disabled
                            InputProps={{ readOnly: true }}
                        />
                    </Grid>
                    <Grid item xs={12} md={4}>
                        <TextField
                            label={String(
                                t("itemDetailPage.fields.purchasePrice")
                            )}
                            value={form.purchasePrice}
                            fullWidth
                            type="number"
                            inputProps={{ step: "0.01", min: "0" }}
                            disabled={!isEditing || saving}
                            onChange={(e) =>
                                setForm((prev) => ({
                                    ...prev,
                                    purchasePrice: e.target.value,
                                }))
                            }
                        />
                    </Grid>
                </Grid>
            </Paper>

            <Box sx={{ mb: 2 }}>
                <SimpleSalesStats
                    title={String(t("itemDetailPage.stats.title"))}
                    loading={statsLoading}
                    revenueTotalMinor={stats?.revenue_total_minor || 0}
                    monthly={(stats?.monthly_sales || []).map((row) => ({
                        month: row.month,
                        revenueMinor: Number(row.revenue_minor || 0),
                    }))}
                    emptyMessage={String(t("itemDetailPage.stats.empty"))}
                />
            </Box>

            <Paper sx={{ p: 3, border: "1px solid #e5e7eb", mb: 2 }}>
                <Typography
                    variant="subtitle2"
                    color="text.secondary"
                    sx={{ mb: 1 }}
                >
                    {t("itemDetailPage.margins.title")}
                </Typography>
                <Grid container spacing={2}>
                    <Grid item xs={12} sm={6}>
                        <Typography sx={{ fontWeight: 600 }}>
                            {t("itemDetailPage.margins.unit")}
                        </Typography>
                        <Typography color="text.secondary">
                            {stats?.margin_unit_minor == null
                                ? t("common.dash")
                                : formatMinor(stats.margin_unit_minor)}
                        </Typography>
                    </Grid>
                    <Grid item xs={12} sm={6}>
                        <Typography sx={{ fontWeight: 600 }}>
                            {t("itemDetailPage.margins.total")}
                        </Typography>
                        <Typography color="text.secondary">
                            {formatMinor(stats?.margin_total_minor || 0)}
                        </Typography>
                    </Grid>
                </Grid>
            </Paper>

            <Paper sx={{ p: 3, border: "1px solid #e5e7eb", mb: 2 }}>
                <Typography
                    variant="subtitle2"
                    color="text.secondary"
                    sx={{ mb: 1 }}
                >
                    {t("itemDetailPage.pricing.resolution.title")}
                </Typography>
                <Grid container spacing={2}>
                    <Grid item xs={12} sm={6}>
                        <Typography sx={{ fontWeight: 600 }}>
                            {t(
                                "itemDetailPage.pricing.resolution.activeSource"
                            )}
                        </Typography>
                        <Typography color="text.secondary">
                            {resolvedSourceLabel}
                        </Typography>
                    </Grid>
                    <Grid item xs={12} sm={6}>
                        <Typography sx={{ fontWeight: 600 }}>
                            {t(
                                "itemDetailPage.pricing.resolution.resolvedPrice"
                            )}
                        </Typography>
                        <Typography color="text.secondary">
                            {resolvedPrice
                                ? formatMinor(resolvedPrice.unit_price_minor)
                                : t("common.dash")}
                        </Typography>
                    </Grid>
                </Grid>
            </Paper>

            <Box sx={{ mb: 2 }}>
                <PricingGridTable
                    title={String(t("itemDetailPage.pricing.title"))}
                    rows={pricingRows}
                    customerTypes={customerTypeOptions}
                    loading={pricingLoading}
                    onCreate={handleCreatePricingRule}
                    onUpdate={handleUpdatePricingRule}
                    onDelete={handleDeletePricingRule}
                />
            </Box>

            <Paper sx={{ p: 3, border: "1px solid #e5e7eb" }}>
                <Typography
                    variant="subtitle2"
                    color="text.secondary"
                    sx={{ mb: 1 }}
                >
                    {t("itemDetailPage.readOnly.title")}
                </Typography>
                <Grid container spacing={2}>
                    <Grid item xs={12} sm={3}>
                        <Typography sx={{ fontWeight: 600 }}>
                            {t("itemDetailPage.readOnly.category")}
                        </Typography>
                        <Typography color="text.secondary">
                            {categoryName}
                        </Typography>
                    </Grid>
                    <Grid item xs={12} sm={3}>
                        <Typography sx={{ fontWeight: 600 }}>
                            {t("itemDetailPage.readOnly.outlet")}
                        </Typography>
                        <Typography color="text.secondary">
                            {outletName}
                        </Typography>
                    </Grid>
                    <Grid item xs={12} sm={3}>
                        <Typography sx={{ fontWeight: 600 }}>
                            {t("itemDetailPage.readOnly.salePrice")}
                        </Typography>
                        <Typography color="text.secondary">
                            {formatMinor(item.unit_price_minor)}
                        </Typography>
                    </Grid>
                    <Grid item xs={12} sm={3}>
                        <Typography sx={{ fontWeight: 600 }}>
                            {t("itemDetailPage.readOnly.purchasePrice")}
                        </Typography>
                        <Typography color="text.secondary">
                            {formatMinor(item.purchase_price_minor)}
                        </Typography>
                    </Grid>
                    <Grid item xs={12} sm={3}>
                        <Typography sx={{ fontWeight: 600 }}>
                            {t("itemDetailPage.readOnly.stockAlert")}
                        </Typography>
                        <Typography
                            color={
                                item.is_stock_alert
                                    ? "error.main"
                                    : "text.secondary"
                            }
                        >
                            {item.is_stock_alert
                                ? t("itemDetailPage.status.active")
                                : t("itemDetailPage.fields.none")}
                        </Typography>
                    </Grid>
                    <Grid item xs={12} sm={3}>
                        <Typography sx={{ fontWeight: 600 }}>
                            {t("itemDetailPage.readOnly.threshold")}
                        </Typography>
                        <Typography color="text.secondary">
                            {item.stock_alert_threshold ?? t("common.dash")}
                        </Typography>
                    </Grid>
                </Grid>
            </Paper>

            <Snackbar
                open={snackbar.open}
                autoHideDuration={3500}
                onClose={() =>
                    setSnackbar((prev) => ({ ...prev, open: false }))
                }
                anchorOrigin={{ vertical: "bottom", horizontal: "center" }}
            >
                <Alert
                    severity={snackbar.severity}
                    variant="filled"
                    onClose={() =>
                        setSnackbar((prev) => ({ ...prev, open: false }))
                    }
                >
                    {snackbar.message}
                </Alert>
            </Snackbar>
        </Box>
    )
}

export default ItemDetailPage
